import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Typography,
    Paper,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
} from '@mui/material';
import { CloudUpload, Refresh, Image as ImageIcon, AddPhotoAlternate, Close, Delete } from '@mui/icons-material';
import { prescriptionModel } from '../Axios/AxiosConfig';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const PromotionUpload = () => {
    const getKioskUID = () => {
        const kioskDetailsCookie = Cookies.get('kiosk_details');
        if (kioskDetailsCookie) {
            try {
                const details = JSON.parse(kioskDetailsCookie);
                return details.UID || details.id || '';
            } catch (error) {
                console.error('Error parsing kiosk_details cookie:', error);
            }
        }
        return ''; 
    };

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loadingImages, setLoadingImages] = useState(false);
    const [images, setImages] = useState([]);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (getKioskUID()) {
            fetchImages();
        }
    }, []);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const fetchImages = async () => {
        setLoadingImages(true);
        try {
            const response = await prescriptionModel.get(`/KioskVarmaPromotion/${getKioskUID()}`);
            if (response.status === 200) {
                setImages(Array.isArray(response.data) ? response.data : [response.data]);
            }
        } catch (error) {
            console.error('Error fetching images:', error);
            toast.error('Failed to fetch images');
            setImages([]);
        } finally {
            setLoadingImages(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.warn('Please select an image to upload');
            return;
        }

        setUploading(true);
        try {
            const base64Image = previewUrl.split(',')[1];
            const payload = {
                UID: getKioskUID(),
                base64Image,
                imageName: selectedFile.name,
            };

            const response = await prescriptionModel.post('/KioskVarmaPromotion', payload);
            if (response.status === 200) {
                toast.success('Image uploaded successfully');
                setSelectedFile(null);
                setPreviewUrl(null);
                fetchImages();
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.message || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleImageClick = (imageUrl, index) => {
        setSelectedPreviewImage({ url: imageUrl, index });
        setPreviewDialogOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewDialogOpen(false);
        setSelectedPreviewImage(null);
    };

    const handleDeleteClick = (event, url) => {
        event.stopPropagation();
        setImageToDelete(url);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setImageToDelete(null);
    };

    const confirmDelete = async () => {
        if (!imageToDelete) return;
        setDeleting(true);
        try {
            const urlParts = imageToDelete.split('/');
            const imageName = urlParts[urlParts.length - 1];

            const response = await prescriptionModel.post('/deleteKioskVarmaPromotion', {
                UID: getKioskUID(),
                imageName: imageName
            });

            if (response.status === 200) {
                toast.success('Image deleted successfully');
                setImages(prev => prev.filter(img => img !== imageToDelete));
                handleCloseDeleteDialog();
            }
        } catch (error) {
            console.error('Error deleting image:', error);
            toast.error(error.response?.data?.message || 'Failed to delete image');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, mb: 8 }}>

            <Box sx={{ mb: 4 }}>
                <Typography
                    sx={{
                        fontFamily: 'Libre Baskerville, serif',
                        fontWeight: 700,
                        fontSize: '24px',
                        color: 'var(--themePrimaryColor, #312110)',
                        mb: 1
                    }}
                >
                    Kiosk Promotion Assets
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Upload Section */}
                <Grid item xs={12} lg={5}>
                    <Card
                        sx={{
                            borderRadius: '8px',
                            backgroundColor: 'var(--themeSecondaryColor, #efefe7)',
                            boxShadow: 'none',
                            border: 'none'
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '18px',
                                    color: 'var(--themePrimaryColor, #3B362A)',
                                    mb: 3
                                }}
                            >
                                Upload New Promotion
                            </Typography>

                            <Box
                                sx={{
                                    border: '2px dashed #D4CEC3',
                                    borderRadius: '8px',
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: 'var(--themeSecondaryColor, #fbf1e7)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: 'var(--themePrimaryColor, #DA6E0B)',
                                        backgroundColor: 'var(--themeSecondaryColor, #f4ecd6)',
                                        transform: 'translateY(-2px)'
                                    },
                                    minHeight: '280px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                onClick={() => document.getElementById('file-input').click()}
                            >
                                <input
                                    type="file"
                                    id="file-input"
                                    hidden
                                    onChange={handleFileChange}
                                    accept="image/*"
                                />
                                {previewUrl ? (
                                    <Box sx={{ position: 'relative', width: '100%' }}>
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '240px',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                mt: 2,
                                                fontFamily: 'Poppins, sans-serif',
                                                fontSize: '12px',
                                                color: 'var(--themePrimaryColor, #312110)',
                                                fontWeight: 500
                                            }}
                                        >
                                            {selectedFile?.name}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <AddPhotoAlternate
                                            sx={{
                                                fontSize: 64,
                                                color: 'var(--themePrimaryColor, #DA6E0B)',
                                                mb: 2,
                                                opacity: 0.8
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily: 'Poppins, sans-serif',
                                                fontWeight: 500,
                                                fontSize: '16px',
                                                color: 'var(--themePrimaryColor, #3B362A)',
                                                mb: 1
                                            }}
                                        >
                                            Click to upload promotional image
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Poppins, sans-serif',
                                                fontSize: '13px',
                                                color: '#7C7870'
                                            }}
                                        >
                                            Supports JPG, PNG, WEBP
                                        </Typography>
                                    </>
                                )}
                            </Box>

                            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleUpload}
                                    disabled={uploading || !selectedFile}
                                    startIcon={uploading ? null : <CloudUpload />}
                                    sx={{
                                        bgcolor: 'var(--themePrimaryColor, #DA6E0B)',
                                        py: 1.5,
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        textTransform: 'none',
                                        borderRadius: '6px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            bgcolor: '#bf5d0a',
                                            boxShadow: '0 4px 12px rgba(218, 110, 11, 0.3)'
                                        },
                                        '&:disabled': {
                                            bgcolor: '#D4CEC3',
                                            color: '#7C7870'
                                        }
                                    }}
                                >
                                    {uploading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Upload Image'}
                                </Button>
                                {selectedFile && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedFile(null);
                                            setPreviewUrl(null);
                                        }}
                                        disabled={uploading}
                                        sx={{
                                            color: 'var(--themePrimaryColor, #312110)',
                                            borderColor: '#D4CEC3',
                                            fontFamily: 'Poppins, sans-serif',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            textTransform: 'none',
                                            px: 3,
                                            '&:hover': {
                                                bordercolor: 'var(--themePrimaryColor, #312110)',
                                                bgcolor: 'transparent'
                                            }
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Gallery Section */}
                <Grid item xs={12} lg={7}>
                    <Card
                        sx={{
                            borderRadius: '8px',
                            backgroundColor: 'var(--themeSecondaryColor, #efefe7)',
                            boxShadow: 'none',
                            minHeight: '500px'
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        fontSize: '18px',
                                        color: 'var(--themePrimaryColor, #3B362A)'
                                    }}
                                >
                                    Promotion Gallery
                                </Typography>
                                <IconButton
                                    onClick={fetchImages}
                                    disabled={loadingImages || !getKioskUID()}
                                    sx={{
                                        color: 'var(--themePrimaryColor, #DA6E0B)',
                                        '&:hover': {
                                            bgcolor: 'var(--themeSecondaryColor, #fbf1e7)'
                                        },
                                        '&:disabled': {
                                            color: '#D4CEC3'
                                        }
                                    }}
                                >
                                    <Refresh />
                                </IconButton>
                            </Box>

                            {loadingImages ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                                    <CircularProgress sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
                                </Box>
                            ) : images.length > 0 ? (
                                <Grid container spacing={2}>
                                    {images.map((url, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Paper
                                                elevation={0}
                                                onClick={() => handleImageClick(url, index)}
                                                sx={{
                                                    border: '1px solid #D4CEC3',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#fff',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                                        borderColor: 'var(--themePrimaryColor, #DA6E0B)'
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={url}
                                                    alt={`Promotion ${index + 1}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '200px',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <Box sx={{ p: 1, px: 1.5, bgcolor: 'var(--themeSecondaryColor, #fbf1e7)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins, sans-serif',
                                                            fontSize: '12px',
                                                            color: 'var(--themePrimaryColor, #312110)',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        Promotion #{index + 1}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleDeleteClick(e, url)}
                                                        sx={{
                                                            color: '#d32f2f',
                                                            padding: '4px',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(211, 47, 47, 0.08)'
                                                            }
                                                        }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '400px',
                                        backgroundColor: 'var(--themeSecondaryColor, #fbf1e7)',
                                        borderRadius: '8px',
                                        border: '2px dashed #D4CEC3'
                                    }}
                                >
                                    <ImageIcon sx={{ fontSize: 72, color: '#D4CEC3', mb: 2 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '16px',
                                            color: '#7C7870',
                                            fontWeight: 500,
                                            textAlign: 'center',
                                            px: 2
                                        }}
                                    >
                                        {getKioskUID() ? 'No promotional images found' : 'No UID available'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '13px',
                                            color: '#7C7870',
                                            mt: 1
                                        }}
                                    >
                                        Upload your first image to get started
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog
                open={previewDialogOpen}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '90vw',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        borderRadius: '8px'
                    }}
                >
                    {/* Close Button */}
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            color: '#fff',
                            bgcolor: 'rgba(218, 110, 11, 0.8)',
                            zIndex: 1,
                            '&:hover': {
                                bgcolor: 'var(--themePrimaryColor, #DA6E0B)'
                            }
                        }}
                    >
                        <Close />
                    </IconButton>

                    {/* Image Info */}
                    {selectedPreviewImage && (
                        <>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 16,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    bgcolor: 'rgba(117, 98, 63, 0.9)',
                                    px: 3,
                                    py: 1.5,
                                    borderRadius: '6px',
                                    zIndex: 1
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: '14px',
                                        color: '#fff',
                                        fontWeight: 500
                                    }}
                                >
                                    Promotion #{selectedPreviewImage.index + 1}
                                </Typography>
                            </Box>

                            {/* Full Image */}
                            <img
                                src={selectedPreviewImage.url}
                                alt={`Promotion ${selectedPreviewImage.index + 1} - Full View`}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '85vh',
                                    objectFit: 'contain',
                                    borderRadius: '8px'
                                }}
                            />
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        width: '100%',
                        maxWidth: '400px'
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: 'var(--themePrimaryColor, #3B362A)' }}>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', color: 'var(--themePrimaryColor, #312110)' }}>
                        Are you sure you want to delete this promotional image? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, px: 3 }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        disabled={deleting}
                        sx={{
                            color: 'var(--themePrimaryColor, #312110)',
                            textTransform: 'none',
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        disabled={deleting}
                        variant="contained"
                        sx={{
                            bgcolor: '#d32f2f',
                            textTransform: 'none',
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#b71c1c',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {deleting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default PromotionUpload;
